import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiURL } from '../../../../utilities/URLs'
import { account } from '../../../../classes/Account'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { CheckCircle, XCircle, ArrowLeft } from 'lucide-react'

const QUARTER_LABELS = {
  q12020: 'Q1 2020',
  q22020: 'Q2 2020',
  q32020: 'Q3 2020',
  q42020: 'Q4 2020',
  q12021: 'Q1 2021',
  q22021: 'Q2 2021',
  q32021: 'Q3 2021',
  q42021: 'Q4 2021'
}

export default function VerifyRefundPage() {
  const navigate = useNavigate()
  const { applicationId, quarter } = useParams()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [submittedValue, setSubmittedValue] = useState(null)
  const [error, setError] = useState('')

  const [refundAmount, setRefundAmount] = useState(null)
  const [refundDate, setRefundDate] = useState(null)
  const [isLoadingDetails, setIsLoadingDetails] = useState(true)

  const quarterLabel = QUARTER_LABELS[quarter] || quarter

  const fmtCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2
  }).format(value)

  const fmtDate = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  }

  useEffect(() => {
    const fetchRefundDetails = async () => {
      try {
        const res = await fetch(`${apiURL}/api/applications/${applicationId}/quarter-breakdown/${quarter}/verify`, {
          headers: { 'Authorization': account.accessToken }
        })
        const data = await res.json()

        if (data.response.error) {
          if (data.response.error === 'Failed To Authenticate Token.') {
            const refreshedToken = await refreshAccessToken()
            if (refreshedToken && refreshedToken.length > 0) return fetchRefundDetails()
            else return navigate('/', { state: { expired: true } })
          }
          console.error(data.response.error)
        } else {
          setRefundAmount(data.response.refundAmount)
          setRefundDate(data.response.refundDate)
        }
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoadingDetails(false)
      }
    }

    fetchRefundDetails()
  }, [applicationId, quarter])

  /**
   * @param {Boolean} hasReceivedRefund
   */
  const handleSubmitVerification = async (hasReceivedRefund) => {
    setIsSubmitting(true)
    setError('')

    try {
      const verifyResponse = await fetch(`${apiURL}/api/applications/${applicationId}/quarter-breakdown/${quarter}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': account.accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hasReceivedRefund })
      })
      const verifyData = await verifyResponse.json()

      if (verifyData.response.error) {
        if (verifyData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          if (refreshedToken && refreshedToken.length > 0)
            return handleSubmitVerification(hasReceivedRefund)
          else
            return navigate('/', { state: { expired: true } })
        }
        setError('Something went wrong. Please try again.')
      } else {
        if (!hasReceivedRefund) {
          // Auto-redirect to 3911 sign page
          navigate(`/applications/${applicationId}/e-sign?type=3911&quarter=${quarter}`)
          return
        }
        setIsSubmitted(true)
        setSubmittedValue(hasReceivedRefund)
      }
    } catch {
      setError('Something went wrong. Please try again.')
    }

    setIsSubmitting(false)
  }

  return (
    <div className='w-full min-h-full flex flex-col items-center p-6 pb-16 text-slate-900 dark:text-white transition-colors duration-300'>
      <div className='w-full flex flex-col items-center mt-8'>

        {/* Back Navigation */}
        <button
          onClick={() => navigate(`/applications/${applicationId}/quarter-breakdown`)}
          className='self-start flex items-center gap-2 text-gray-500 hover:text-gray-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors mb-6 cursor-pointer'
        >
          <ArrowLeft className='w-4 h-4' />
          <span className='text-sm'>Back to Quarter Breakdown</span>
        </button>

        {isLoadingDetails ? (
          <div className='w-full flex flex-col items-center gap-4'>
            <div className='h-8 w-64 bg-gray-200 dark:bg-slate-700 rounded animate-pulse' />
            <div className='h-4 w-32 bg-gray-200 dark:bg-slate-700 rounded animate-pulse' />
            <div className='w-full border-2 border-gray-200 dark:border-slate-700 rounded-xl p-8'>
              <div className='h-5 w-full bg-gray-200 dark:bg-slate-700 rounded animate-pulse mb-3' />
              <div className='h-5 w-3/4 bg-gray-200 dark:bg-slate-700 rounded animate-pulse mb-3 mx-auto' />
              <div className='h-5 w-5/6 bg-gray-200 dark:bg-slate-700 rounded animate-pulse mb-8 mx-auto' />
              <div className='flex gap-4'>
                <div className='flex-1 h-14 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse' />
                <div className='flex-1 h-14 bg-gray-200 dark:bg-slate-700 rounded-lg animate-pulse' />
              </div>
            </div>
          </div>
        ) : !isSubmitted ? (
          <div className='w-full flex flex-col items-center'>
            {/* Header */}
            <div className='mb-8 text-center'>
              <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-3'>
                Verify Refund Check
              </h1>
              <p className='text-gray-600 dark:text-slate-300'>
                {quarterLabel}
              </p>
            </div>

            {/* Question Card */}
            <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-8 transition-colors duration-300'>
              <p className='text-lg text-gray-800 dark:text-slate-200 text-center mb-8 font-medium leading-relaxed'>
                {refundAmount && refundDate ? (
                  <>
                    IRS transcripts show that a refund of{' '}
                    <span className='font-bold text-gray-900 dark:text-white'>{fmtCurrency(refundAmount)}</span>{' '}
                    was issued on{' '}
                    <span className='font-bold text-gray-900 dark:text-white'>{fmtDate(refundDate)}</span>.{' '}
                    Please confirm that you have or have not received this amount. Please check your bank statements, or anywhere you may have deposited this check.
                  </>
                ) : (
                  <>
                    Have you received your refund check from the IRS for {quarterLabel}?
                  </>
                )}
              </p>

              {/* Buttons */}
              <div className='flex flex-col sm:flex-row gap-4'>
                <button
                  onClick={() => handleSubmitVerification(true)}
                  disabled={isSubmitting}
                  className='flex-1 flex items-center justify-center gap-3 py-4 px-6 bg-green-600 hover:bg-green-700 disabled:bg-green-400 dark:disabled:bg-green-800 text-white font-semibold rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed'
                >
                  <CheckCircle className='w-5 h-5' />
                  Yes, I received it
                </button>
                <button
                  onClick={() => handleSubmitVerification(false)}
                  disabled={isSubmitting}
                  className='flex-1 flex items-center justify-center gap-3 py-4 px-6 bg-red-600 hover:bg-red-700 disabled:bg-red-400 dark:disabled:bg-red-800 text-white font-semibold rounded-lg transition-colors cursor-pointer disabled:cursor-not-allowed'
                >
                  <XCircle className='w-5 h-5' />
                  No, I have not
                </button>
              </div>

              {/* Loading State */}
              {isSubmitting && (
                <div className='flex flex-row items-center justify-center gap-2 mt-6'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
                  <p className='text-sm text-gray-500 dark:text-slate-400'>Submitting...</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <p className='text-red-600 dark:text-red-400 text-sm text-center mt-4'>{error}</p>
              )}
            </div>
          </div>
        ) : (
          /* Success State */
          <div className='w-full flex flex-col items-center'>
            <div className='w-full bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-xl p-8 text-center transition-colors duration-300'>
              <div className='flex justify-center mb-6'>
                {submittedValue ? (
                  <CheckCircle className='w-16 h-16 text-green-600 dark:text-green-500' />
                ) : (
                  <XCircle className='w-16 h-16 text-red-500 dark:text-red-400' />
                )}
              </div>
              <h2 className='text-2xl font-bold text-gray-900 dark:text-white mb-3'>
                Response Recorded
              </h2>
              <p className='text-gray-600 dark:text-slate-300 mb-8'>
                {submittedValue
                  ? 'Thank you for confirming. We have received your answer and your records have been updated.'
                  : 'Thank you for letting us know. Our team is working on creating a 3911 for you to sign.'
                }
              </p>
              <button
                onClick={() => navigate(`/applications/${applicationId}/quarter-breakdown`)}
                className='py-3 px-8 bg-ertc-primary hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors cursor-pointer'
              >
                Back to Quarter Breakdown
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
